import { useForm, SubmitHandler } from 'react-hook-form';
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export interface SearchFormValues {
  searchQuery: string;
}

export interface SearchPatientFormProps {
  onSubmit: SubmitHandler<SearchFormValues>;
  placeholder?: string;
}

export default function SearchPatientForm({ onSubmit, placeholder }: SearchPatientFormProps) {
  const form = useForm<SearchFormValues>({ defaultValues: { searchQuery: '' } });
  const { watch } = form;
  const queryValue = watch('searchQuery');

  // debounce your onSubmit so it only fires 300ms after the last change
  const debouncedSubmit = useDebouncedCallback((val: string) => onSubmit({ searchQuery: val }), 10);

  useEffect(() => {
    debouncedSubmit(queryValue);
  }, [queryValue, debouncedSubmit]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex w-full max-w-xl flex-row items-center gap-2 rounded-2xl border border-content-highlight/40 bg-content/80 p-2 text-white shadow-inner shadow-primary/10"
      >
        <FormField
          control={form.control}
          name="searchQuery"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <Input
                  placeholder={placeholder || ''}
                  className="h-11 w-full border-0 bg-input/80 text-sm text-white placeholder:text-white/40"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <button
          type="submit"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-content-highlight/40 bg-content-dark/70 transition hover:border-primary/50 hover:bg-primary/20"
        >
          <Search />
        </button>
      </form>
    </Form>
  );
}
